import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import moment from "moment";
import { useFormikContext } from "formik";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { usePopupState, bindTrigger, bindPopover } from "material-ui-popup-state/hooks";
import InputAdornment from "@mui/material/InputAdornment";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";

const YEAR_VIEW = "year";
const MONTH_VIEW = "month";

const styles = {
  button: {
    px: 0,
    py: 0.5,
    borderRadius: 5,
  },
};

const getButtonStyle = isSelected => ({
  color: isSelected ? "white" : "black",
  backgroundColor: isSelected ? "primary.main" : "transparent",
  "&:hover": {
    backgroundColor: isSelected ? "primary.dark" : "action.hover",
  },
});

export default function MonthYearPicker({ name, label, disabled }) {
  const { values, setFieldValue, errors, touched, submitCount } = useFormikContext();
  const popupState = usePopupState({ variant: "popover", popupId: `month-year-picker-${name}` });
  const [viewMode, setViewMode] = useState(MONTH_VIEW);
  const [selectedMonth, setSelectedMonth] = useState("");
  const errorMessage = errors[name];
  const showError = (touched[name] || submitCount > 0) && errorMessage;
  const currentYear = moment().year();
  const years = Array.from({ length: 12 }, (_, i) => currentYear - 5 + i);

  const handleClose = () => {
    popupState.close();
    setViewMode(MONTH_VIEW);
  };

  const handleMonthSelect = month => {
    setSelectedMonth(month);
    setViewMode(YEAR_VIEW);
  };

  const handleYearSelect = year => {
    setFieldValue(name, `${selectedMonth}/${year}`);
    handleClose();
  };

  const handleNASelect = () => {
    setFieldValue(name, "N/A");
    handleClose();
  };

  const getMonthYearDisplay = value => {
    if (value === "N/A") return "N/A";
    if (!value) return "";
    return value;
  };

  const renderContent = () => {
    if (viewMode === MONTH_VIEW) {
      const monthValue = values[name]?.split("/")[0];

      return (
        <Box>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography fontWeight={500}>Select Month</Typography>
            </Grid>
            {Array.from({ length: 12 }, (_, i) => {
              const monthNum = String(i + 1).padStart(2, "0");
              const monthName = moment()
                .month(i)
                .format("MMM");
              const isSelected = monthValue === monthNum;

              return (
                <Grid key={i} size={4}>
                  <Button
                    fullWidth
                    onClick={() => handleMonthSelect(monthNum)}
                    sx={{
                      ...styles.button,
                      ...getButtonStyle(isSelected),
                    }}
                  >
                    {`${monthName} (${monthNum})`}
                  </Button>
                </Grid>
              );
            })}
            <Grid sx={{ mt: 1, display: "flex", justifyContent: "center" }} size={12}>
              <Button
                variant={values[name] === "N/A" ? "contained" : "text"}
                onClick={handleNASelect}
                sx={{
                  ...styles.button,
                  ...getButtonStyle(values[name] === "N/A"),
                }}
              >
                N/A
              </Button>
            </Grid>
          </Grid>
        </Box>
      );
    } else {
      return (
        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setViewMode(MONTH_VIEW)}
              size="small"
            >
              Back
            </Button>
            <Typography fontWeight={500} sx={{ ml: 2 }}>
              {moment()
                .month(parseInt(selectedMonth) - 1)
                .format("MMM")}{" "}
              ({selectedMonth})
            </Typography>
          </Box>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography fontWeight={500}>Select Year</Typography>
            </Grid>
            {years.map(year => (
              <Grid key={year} size={4}>
                <Button
                  fullWidth
                  onClick={() => handleYearSelect(year)}
                  sx={{
                    ...styles.button,
                    ...getButtonStyle(false),
                  }}
                >
                  {year}
                </Button>
              </Grid>
            ))}
            <Grid display="flex" justifyContent="center" size={12}>
              <Button
                variant={values[name] === "N/A" ? "contained" : "text"}
                onClick={handleNASelect}
                sx={{
                  ...styles.button,
                  ...getButtonStyle(values[name] === "N/A"),
                }}
              >
                N/A
              </Button>
            </Grid>
          </Grid>
        </Box>
      );
    }
  };

  return (
    <div>
      <TextField
        label={label}
        fullWidth
        size="small"
        value={getMonthYearDisplay(values[name])}
        error={!!showError}
        helperText={showError || ""}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <InsertInvitationIcon color={disabled ? "disabled" : "action"} />
            </InputAdornment>
          ),
        }}
        disabled={disabled}
        {...bindTrigger(disabled ? {} : popupState)}
      />

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handleClose}
      >
        <Box sx={{ p: 2, width: 280 }}>{renderContent()}</Box>
      </Popover>
    </div>
  );
}
